/**
 * #408 — what a message says when its retries run out.
 *
 * The cap used to write only "Max retries exhausted (N/N)", which dropped WHY every attempt
 * failed. In the #403 failure taxonomy, 2,606 failed sends in 7 days were really WhatsApp never
 * acknowledging the message: the store check found the copy at ack -1 on each retry, the ack-gate
 * correctly refused to call it delivered and re-sent, and at the cap the cause vanished behind
 * the cap text and a stale backend reason (`no-mqtt`).
 *
 * Precedence: store evidence from this retry (ack < 1) beats anything carried on the doc;
 * otherwise the doc's prior cause is kept; only when nothing is known does the bare cap text stand.
 * Pure so it is unit-testable without a store, a clock, or the network.
 */

/** Curated `sendFailureReason`: the copy reached WhatsApp's store but was never acked (ack < 1). */
export const WA_NOT_ACKED_FAILURE_REASON = 'wa_not_acked';

/**
 * #503 — the page-side send never settled inside the 180s guard (`Timeout in sendMessageViaWhatsapp after N ms`). The
 * engine was connected and the message was handed to it, so the BE's dispatch-time `no-mqtt` / `mqtt-failed-to-deliver`
 * routing label is stale by the time the retries run out.
 */
export const SEND_TIMEOUT_FAILURE_REASON = 'send-timeout';

/**
 * #777 — WhatsApp's server refused the stanza (e.g. error=403). Typed by the backend (`WA_REJECTED_STANZA_REASON`,
 * failure-cause-class.ts; keep the string in sync). The refusal is the cause, so it outranks ack evidence at the cap.
 */
export const WA_REJECTED_STANZA_FAILURE_REASON = 'wa-rejected-stanza';

const SEND_TIMEOUT_ERROR_RE = /^Timeout in sendMessageViaWhatsapp after \d+ ?ms\b/i;
const ROUTING_LABELS: ReadonlySet<string> = new Set(['no-mqtt', 'mqtt-failed-to-deliver']);

export interface RetryCapFailureInput {
  currentRetryCount: number;
  maxRetries: number;
  /** The ack of the message's copy in WhatsApp's store at this retry's store check; undefined = no match. */
  lastStoreCheckAck: number | undefined;
  /** The failure fields already on the doc (from the last attempt). */
  prior: { sendFailureReason?: unknown; sendFailureError?: unknown };
}

export interface RetryCapFailure {
  sendFailureError: string;
  /** Set only when it should change; absent leaves the doc's reason as it is. */
  sendFailureReason?: string;
}

function errorText(error: unknown): string | undefined {
  // An engine error object coerced to a string carries no cause; treat it as unknown.
  if (typeof error === 'string') return (error.trim() && error.trim() !== '[object Object]') ? error.trim() : undefined;
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message.trim() || undefined;
  }
  return undefined;
}

/** Engine diagnostics ("... | store-at-failure: ...") are forensic only; the cause is what precedes the first " | ". */
const stripDiagnostics = (text: string): string => text.split(' | ')[0].trim();

/** A curated code (`rate_limited`, `not_on_whatsapp`) is snake_case; routing labels (`no-mqtt`) and raw errors are not. */
const isCuratedReason = (reason: string): boolean => /^[a-z0-9_]+$/.test(reason);

export function resolveRetryCapFailure({
  currentRetryCount,
  maxRetries,
  lastStoreCheckAck,
  prior,
}: RetryCapFailureInput): RetryCapFailure {
  const capText = `Max retries exhausted (${currentRetryCount}/${maxRetries})`;
  const priorReason = typeof prior.sendFailureReason === 'string' && prior.sendFailureReason ? prior.sendFailureReason : undefined;
  const rawPriorError = errorText(prior.sendFailureError);
  const priorError = rawPriorError && !rawPriorError.startsWith('Max retries exhausted') ? stripDiagnostics(rawPriorError) : undefined;
  const withLastError = (head: string) => (priorError ? `${head}. Last error: ${priorError}` : head);

  if (lastStoreCheckAck !== undefined && lastStoreCheckAck < 1) {
    // Neutral wording: a store ack of 0 can be a default and -1 can be a local outbox state, so claim only what was seen.
    const seen = `the message was not acknowledged by WhatsApp (store ack ${lastStoreCheckAck})`;
    const sendFailureError = withLastError(`${capText}: ${seen}`);
    // A curated reason the UI already renders (rate limit, not on WhatsApp) outranks the ack evidence.
    if (priorReason && (isCuratedReason(priorReason) || priorReason === WA_REJECTED_STANZA_FAILURE_REASON)) return { sendFailureReason: priorReason, sendFailureError };
    return { sendFailureReason: WA_NOT_ACKED_FAILURE_REASON, sendFailureError };
  }

  const sendFailureError = withLastError(capText);
  if (priorError && SEND_TIMEOUT_ERROR_RE.test(priorError) && (!priorReason || ROUTING_LABELS.has(priorReason))) {
    return { sendFailureReason: SEND_TIMEOUT_FAILURE_REASON, sendFailureError };
  }
  return priorReason ? { sendFailureReason: priorReason, sendFailureError } : { sendFailureError };
}
